import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Phone, MessageSquare, CalendarDays, UserX, Stethoscope, RotateCcw } from 'lucide-react';

const WORKFLOWS = [
  { icon: Phone, label: 'Instant lead response' },
  { icon: MessageSquare, label: 'Missed-call text recovery' },
  { icon: CalendarDays, label: 'Consultation confirmation' },
  { icon: UserX, label: 'No-show reactivation' },
  { icon: Stethoscope, label: 'Post-consult treatment follow-up' },
  { icon: RotateCcw, label: 'Dormant lead re-engagement' },
];

export default function About() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-xl border-b border-slate-100 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 h-16">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center">
              <span className="text-white text-xs font-bold">DR</span>
            </div>
            <span className="text-lg font-bold text-slate-900 tracking-tight">Digital Reign</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => navigate('/')} className="text-sm text-slate-600 hover:text-slate-900 transition-colors">Home</button>
            <button onClick={() => navigate('/#features')} className="text-sm text-slate-600 hover:text-slate-900 transition-colors">Features</button>
            <button onClick={() => navigate('/#pricing')} className="text-sm text-slate-600 hover:text-slate-900 transition-colors">Pricing</button>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/sign-in')} className="text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors px-3 py-2">Sign In</button>
            <button onClick={() => navigate('/sign-up')} className="text-sm font-medium bg-slate-900 text-white px-5 py-2.5 rounded-lg hover:bg-slate-800 transition-all duration-200">Get Started</button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 transition-colors mb-10"
            >
              <ArrowLeft size={14} /> Back to home
            </button>

            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight leading-[1.15]">
              About the Founder
            </h1>
          </motion.div>

          {/* Founder Photo */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-12"
          >
            <div className="relative w-full max-w-sm">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden border border-slate-200 shadow-lg">
                <img
                  src="https://customer-assets.emergentagent.com/job_3da4fd30-2c70-4250-9e4b-59e6541611f4/artifacts/ddskacgo_IMG_20251022_082340_810.webp"
                  alt="Founder of Digital Reign — Digital Reign LLC"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="mt-4">
                <p className="text-sm font-semibold text-slate-900">Marcus Spicer</p>
                <p className="text-xs text-slate-500">Founder & CEO, Digital Reign LLC</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="space-y-8"
          >
            <p className="text-lg text-slate-600 leading-relaxed">
              Digital Reign was built after seeing the same silent problem happen again and again inside high-value service businesses:
            </p>

            <p className="text-xl font-semibold text-slate-900 leading-snug border-l-4 border-slate-900 pl-6 py-2">
              Revenue was being lost in the gaps between patient interest and patient action.
            </p>

            <p className="text-lg text-slate-600 leading-relaxed">
              In cosmetic dentistry, those gaps are expensive.
            </p>

            {/* The three pain points */}
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-8 space-y-4">
              {[
                'A missed call from a veneer lead.',
                'A consultation request that never receives timely follow-up.',
                'A patient who says they "need time to think" and is never nurtured back into treatment.',
              ].map((line, i) => (
                <p key={i} className="text-base text-slate-700 leading-relaxed pl-4 border-l-2 border-slate-300">
                  {line}
                </p>
              ))}
            </div>

            <p className="text-lg text-slate-600 leading-relaxed">
              These aren't small operational issues.
            </p>

            <p className="text-lg text-slate-600 leading-relaxed">
              For cosmetic and veneer-focused clinics, a single missed opportunity can represent <span className="font-semibold text-slate-900">thousands in lost case revenue</span>.
            </p>

            <p className="text-lg text-slate-600 leading-relaxed">
              That realization became the foundation for Digital Reign.
            </p>

            {/* Digital Reign callout */}
            <div className="bg-slate-900 rounded-xl p-8">
              <p className="text-lg text-slate-200 leading-relaxed">
                Built by <span className="font-semibold text-white">Digital Reign LLC</span>, the Digital Reign platform was designed specifically for cosmetic dental practices that want to eliminate revenue leaks, fill empty chair time, and increase treatment acceptance through intelligent automation.
              </p>
            </div>

            <p className="text-lg text-slate-600 leading-relaxed">
              The mission was simple:
            </p>

            <p className="text-xl font-semibold text-slate-900 leading-snug">
              Help practices stop losing high-value patients to delayed responses, missed calls, no-shows, and inconsistent follow-up.
            </p>

            <p className="text-lg text-slate-600 leading-relaxed">
              Every workflow inside the system was created with the cosmetic dental patient journey in mind:
            </p>

            {/* Workflows */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {WORKFLOWS.map((w, i) => (
                <motion.div
                  key={w.label}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08, duration: 0.4 }}
                  viewport={{ once: true }}
                  className="flex items-center gap-4 p-4 bg-white border border-slate-200 rounded-xl hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200"
                >
                  <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center shrink-0">
                    <w.icon size={18} className="text-white" />
                  </div>
                  <span className="text-sm font-medium text-slate-800">{w.label}</span>
                </motion.div>
              ))}
            </div>

            {/* Closing */}
            <div className="pt-4 space-y-6">
              <p className="text-lg text-slate-600 leading-relaxed">
                The goal is not more software.
              </p>

              <p className="text-xl font-semibold text-slate-900 leading-snug">
                The goal is more booked consultations, more accepted treatment plans, and more recovered revenue.
              </p>

              <p className="text-lg text-slate-600 leading-relaxed">
                Digital Reign exists to help modern clinics grow with the speed and consistency today's high-value patients expect.
              </p>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="mt-16 bg-slate-50 rounded-xl border border-slate-200 p-10 text-center"
          >
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
              Ready to stop losing revenue?
            </h2>
            <p className="text-sm text-slate-500 mt-2">
              See exactly where money is leaking from your practice — in minutes.
            </p>
            <button
              data-testid="about-cta-get-started"
              onClick={() => navigate('/sign-up')}
              className="mt-6 inline-flex items-center gap-2 bg-slate-900 text-white px-7 py-3 rounded-lg font-medium text-sm hover:bg-slate-800 transition-all duration-200"
            >
              Get Started <ArrowRight size={16} />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-slate-100">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-slate-900 rounded flex items-center justify-center">
              <span className="text-white text-xs font-bold">DR</span>
            </div>
            <span className="text-sm font-semibold text-slate-900">Digital Reign</span>
          </div>
          <p className="text-xs text-slate-500">Dental Revenue Recovery</p>
        </div>
      </footer>
    </div>
  );
}
